import { useCallback, useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  ArrowBackIos,
  ArrowForwardIos,
  FiberManualRecordOutlined,
  FiberManualRecordRounded,
  PictureInPictureAlt,
  Window,
} from '@mui/icons-material';
import { Drawer, IconButton } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useLocation, useParams } from 'react-router';

import type { ImageObject } from '@/modules/annotator/types';

import {
  VideaButton,
  VideaTextButton,
} from '../../../../../common-components/elements/VideaButton/VideaButton';
import { VideaTypography } from '../../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  selectQuadrantsAtom,
  selectTeethAtom,
} from '../../../../patientList/atoms';
import { sidebarIsNotCollapsedAtom } from '../../../atoms';
import {
  activeImageIsSelectedAtom,
  casePresentationModeAtom,
  selectedFindingsAtom,
} from '../../../casePresentation/atoms';

import {
  enabledImageTypesAtom,
  imageBrowserExpandedAtom,
  imageBrowserOpenAtom,
  showingAllAtom,
} from './atoms';
import {
  getImageBrowserDisplayImages,
  useExtraImages,
  useGetFilteredImages,
} from './helpers';
import { ImageBrowserFilters } from './ImageBrowserFilters';
import { ImageBucket } from './imageBucket/ImageBucket';
import * as Styled from './styles';

export const ImageBrowser = () => {
  const [isImageBrowserOpen, setIsImageBrowserOpen] =
    useAtom(imageBrowserOpenAtom);
  const [isImageBrowserExpanded, setIsImageBrowserExpanded] = useAtom(
    imageBrowserExpandedAtom,
  );
  const { imageId: activeImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const isActiveImage = !!activeImageId;

  const [selectedTeeth, setSelectedTeeth] = useAtom(selectTeethAtom);
  const [selectedQuadrants, setSelectedQuadrants] =
    useAtom(selectQuadrantsAtom);
  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const enabledImageTypes = useAtomValue(enabledImageTypesAtom);
  const isSidebarNotCollapsed = useAtomValue(sidebarIsNotCollapsedAtom);
  const imageBrowserExpanded = useAtomValue(imageBrowserExpandedAtom);

  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const selectedPhotoId = params.get('selected-photo');
  const isActiveImageIsSelected = useAtomValue(activeImageIsSelectedAtom);

  const isFocusOnActiveImage = !selectedPhotoId || isActiveImageIsSelected;

  const [imageBrowserPage, setImageBrowserPage] = useState<number>(1);
  const [numberOfImagesToDisplay, setNumberOfImagesToDisplay] =
    useState<number>(5);

  const images = useGetFilteredImages({});

  const { hiddenImages, outOfScope } = useExtraImages();
  const isShowingAll = useAtomValue(showingAllAtom);
  const presentSingleFinding = useAtomValue(selectedFindingsAtom);

  const showAllButton =
    (hiddenImages || (isShowingAll && outOfScope)) &&
    !imageBrowserExpanded &&
    !presentSingleFinding &&
    !selectedTeeth.length;

  const isActiveImageDentalPhoto =
    images?.find((image) => image.imageId === activeImageId)
      ?.detectedImageType === 'COLOR_IMAGE';

  const displayImages = getImageBrowserDisplayImages({
    numberOfImagesToDisplay,
    images,
    page: imageBrowserPage,
  });

  const numberOfImageSpots = showAllButton
    ? (images?.length ?? 0) + 1
    : (images?.length ?? 0);

  const maxPage = Math.ceil(numberOfImageSpots / numberOfImagesToDisplay);

  const handleImageBrowserPage = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > maxPage) {
      return;
    }
    setImageBrowserPage(pageNumber);
  };

  const handleCloseImageBrowser = () => {
    setIsImageBrowserOpen(!isImageBrowserOpen);
    setIsImageBrowserExpanded(false);
    if (!isCasePresentationMode) {
      setSelectedTeeth([]);
      setSelectedQuadrants([]);
    }
  };

  useEffect(() => {
    const imageIndex = images?.findIndex((image) =>
      isFocusOnActiveImage
        ? activeImageId === image.imageId
        : selectedPhotoId === image.imageId,
    );
    if (activeImageId && imageIndex && imageIndex !== -1) {
      const pageNumber = Math.floor(imageIndex / numberOfImagesToDisplay) + 1;
      setImageBrowserPage(pageNumber);
    } else {
      setImageBrowserPage(1);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency*/
  }, [
    activeImageId,
    selectedPhotoId,
    numberOfImagesToDisplay,
    isImageBrowserExpanded,
    enabledImageTypes,
    setImageBrowserPage,
    selectedTeeth,
    selectedQuadrants,
    isFocusOnActiveImage,
    isShowingAll,
  ]);

  const PageTracker = () => {
    if (numberOfImagesToDisplay < 1) {
      return;
    }
    return [...Array(maxPage)].map((_, i) =>
      i === imageBrowserPage - 1 ? (
        <IconButton
          onClick={() => setImageBrowserPage(i + 1)}
          key={i}
          className="button"
        >
          <FiberManualRecordRounded className="icon" />
        </IconButton>
      ) : (
        <IconButton
          onClick={() => setImageBrowserPage(i + 1)}
          key={i}
          className="button"
        >
          <FiberManualRecordOutlined className="icon" />
        </IconButton>
      ),
    );
  };

  const ref: React.RefCallback<HTMLDivElement> = useCallback(
    (element) => {
      if (element && !imageBrowserExpanded) {
        const rect = element.getBoundingClientRect();
        const numberOfImagesThatFit = isActiveImage
          ? Math.floor((rect.width - 50) / 115)
          : Math.floor((rect.width - 50) / 115) - 1;
        setNumberOfImagesToDisplay(
          numberOfImagesThatFit > 11 ? 11 : numberOfImagesThatFit,
        );
      }
      if (imageBrowserExpanded) {
        setNumberOfImagesToDisplay(6);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps*/
    [
      isSidebarNotCollapsed,
      setNumberOfImagesToDisplay,
      isActiveImageDentalPhoto,
      isActiveImage,
      imageBrowserExpanded,
    ],
  );

  return (
    <>
      {!isImageBrowserOpen && (
        <Styled.ImageBrowserButton count={images?.length}>
          <VideaTextButton
            className="filter-button"
            onClick={() => {
              setIsImageBrowserOpen(!isImageBrowserOpen);
            }}
          >
            <Window />
            <VideaTypography variant="labelSmall">
              <Trans>Image Browser</Trans>
            </VideaTypography>
            <Styled.VideaBadge badgeContent={images?.length} color="success" />
          </VideaTextButton>
        </Styled.ImageBrowserButton>
      )}
      <Styled.ImageBrowserDrawer>
        <Drawer
          anchor="bottom"
          open={isImageBrowserOpen}
          variant="permanent"
          className={isImageBrowserExpanded ? 'expanded-drawer' : 'drawer'}
        >
          <div
            className={
              isImageBrowserExpanded
                ? 'expanded-drawer-container'
                : 'drawer-container'
            }
          >
            {isImageBrowserOpen && (
              <VideaButton
                className="close-button"
                onClick={() => handleCloseImageBrowser()}
              >
                <PictureInPictureAlt className="icon" />
              </VideaButton>
            )}
            {isImageBrowserOpen && (
              <div className="image-layout">
                <ImageBrowserFilters />
                <div className="image-bucket" ref={ref}>
                  {!!images?.length && maxPage > 1 && (
                    <VideaButton
                      className="arrow left"
                      onClick={() =>
                        handleImageBrowserPage(imageBrowserPage - 1)
                      }
                      disabled={imageBrowserPage === 1}
                    >
                      <ArrowBackIos className="icon" />
                    </VideaButton>
                  )}
                  <ImageBucket
                    isImageBrowserOpen={isImageBrowserOpen}
                    otherImages={displayImages as ImageObject[]}
                    lastPage={imageBrowserPage === maxPage}
                  />

                  {maxPage > 1 && (
                    <div className="pages">
                      <PageTracker />
                    </div>
                  )}
                  {!!images?.length && maxPage > 1 && (
                    <VideaButton
                      className="arrow right"
                      onClick={() =>
                        handleImageBrowserPage(imageBrowserPage + 1)
                      }
                      disabled={imageBrowserPage >= maxPage}
                    >
                      <ArrowForwardIos className="icon" />
                    </VideaButton>
                  )}
                </div>
              </div>
            )}
          </div>
        </Drawer>
      </Styled.ImageBrowserDrawer>
    </>
  );
};
